import { QueryClientContext } from "@tanstack/react-query";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

import { type Mountaya, MountayaConfigurationError } from "@mountaya/client";

import { mountayaKeys } from "../rules/mountayaKeys.js";

/**
 * What the provider hands down: the client, and the credential scope it carried when the
 * provider last rendered, with whether that scope was still pending.
 *
 * The scope is read at every render rather than once, because a session token's presence is
 * part of it and changes while the client stays the same object: a first mint lands, a mint
 * fails, a visitor signs out. The value is memoized on all three, so the components beneath
 * re-render when the scope or its pending flag moves and at no other time, and a memo keyed on
 * the client holds still. The flag is its own member because it can move alone: the first token
 * of a source that reports no user settles the scope without changing its value, and every read
 * beneath waits for that settling.
 */
interface MountayaContextValue {
  readonly client: Mountaya;
  readonly pending: boolean;
  readonly scope: string;
}

/**
 * The client context.
 *
 * `null` rather than `undefined` as the empty value, so a component rendered outside the
 * provider fails with a sentence naming the provider instead of with a property access on
 * undefined three frames deeper.
 */
const MountayaContext = createContext<MountayaContextValue | null>(null);

/** MountayaProviderProps is what `MountayaProvider` takes. */
export interface MountayaProviderProps {
  /** The client every hook below will use. Construct it once, outside render. */
  readonly client: Mountaya;
  /** The tree whose hooks read the client. */
  readonly children: ReactNode;
}

/**
 * MountayaProvider makes one client available to the hooks in this package.
 *
 * It deliberately does not construct the client. A client holds a credential and a retry
 * policy, and building one inside a component would rebuild it on every render that
 * touched a prop: quietly discarding nothing, but making the credential a render-time
 * concern when it is an application-lifetime one.
 *
 * It reads the client's credential scope at every render and follows the client's
 * `subscribeCredentialScope`, so a read beneath it is keyed under the session the client carries
 * now: a session token source's every move re-renders it, and a token held as a function is
 * read again each time the component that holds it renders. When the scope of one client
 * changes, the entries read under the scope it left are dropped from the query cache, so an
 * answer read without a session is never served to one. The cache is the one
 * `QueryClientProvider` hands down above this provider: mount this one inside it, since a query
 * client mounted beneath is one it cannot reach.
 *
 * While the scope is pending, from a source's construction until its first token lands, every
 * read of this package beneath it holds, since that token names the person the scope settles on:
 * each read is then keyed and fetched once. A request made before would wait for the token
 * anyway, so holding it costs no time.
 *
 * The scope is read in the render, and the `session` of `useSessionToken` answers for the
 * commit, so for the one commit that changes the session's presence a read made from an effect
 * is keyed under the presence the tree rendered with and sent under the one the commit carries:
 * the first read after a sign-in waits for the token under the anonymous scope, and the last
 * after a sign-out goes out under the key alone under the session's. Both entries are dropped by
 * the render that follows, when the scope moves.
 *
 * Who a write acts as is the session's to say, not this provider's: the user a session token
 * was minted for is the user every call made with it acts as, and the scope names that user, so
 * a change of person moves the scope and drops the reads made for the last one.
 */
export const MountayaProvider = ({ client, children }: MountayaProviderProps): ReactElement => {
  // Followed as well as read, so the tree moves with the session whether or not the component
  // above re-renders: a source's first token, a sign-out, a change of person. Two primitives
  // rather than one object, so each read answers the same value until the client moves it.
  const subscribe = useCallback(
    (onChange: () => void) => client.transport.subscribeCredentialScope(onChange),
    [client],
  );
  const readScope = useCallback(() => client.transport.credentialScope, [client]);
  const readPending = useCallback(() => client.transport.credentialScopePending, [client]);
  const scope = useSyncExternalStore(subscribe, readScope, readScope);
  const pending = useSyncExternalStore(subscribe, readPending, readPending);
  const value = useMemo(() => ({ client, pending, scope }), [client, pending, scope]);
  // Read without `useQueryClient`, which throws: a tree with no query client has no cache to
  // drop anything from, and the provider renders there too.
  const queries = use(QueryClientContext);
  const held = useRef(value);

  // One client whose scope moved changed its session's presence. Its entries under the scope
  // it left were read under the presence it no longer has, or were in flight while it changed,
  // so they are dropped rather than served to the next reader of that scope. Another client is
  // another credential, and keeps its entries for the provider that hands it down again.
  useEffect(() => {
    const previous = held.current;
    held.current = value;

    if (previous.client === value.client && previous.scope !== value.scope) {
      queries?.removeQueries({ queryKey: mountayaKeys.scope(previous.scope) });
    }
  }, [queries, value]);

  return <MountayaContext value={value}>{children}</MountayaContext>;
};

/**
 * useMountaya returns the client from the nearest provider.
 *
 * A component that calls it re-renders when the credential scope of that client changes, or
 * settles, so a key it builds from `client.transport.credentialScope` follows the session it
 * carries.
 *
 * A missing provider is a `MountayaConfigurationError`, the same class the client throws
 * for a credential that cannot work: nothing reached the network, and no retry helps. A
 * bare `Error` would be the one failure in the family an application catching
 * `MountayaError` around its tree had to catch separately, by matching on the message.
 *
 * `@mountaya/elements-react` publishes a `useMountayaClient` that looks like this one and is
 * not: it reads that package's own provider, returns the narrow source interface an element
 * needs, and answers null where this throws. A tree mounting both providers holds two
 * answers, so reaching for the wrong hook is a real mistake: the names differ deliberately.
 *
 * @returns The client the nearest {@link MountayaProvider} hands down.
 * @throws MountayaConfigurationError Outside a {@link MountayaProvider}.
 */
export const useMountaya = (): Mountaya => {
  const provided = use(MountayaContext);

  if (provided === null) {
    throw new MountayaConfigurationError(
      "useMountaya was called outside a <MountayaProvider>. Build one client outside render " +
        "and mount <MountayaProvider client={mountaya}> above this component.",
    );
  }

  return provided.client;
};

/**
 * useMountayaOrNull returns the client from the nearest provider, or null when there is none.
 *
 * The twin of {@link useMountaya}, for the one shape that cannot use it: a component that MOUNTS
 * a provider when there is no client above it and inherits when there is. `useMountaya` throws
 * on the branch that component exists to handle, and a throw is not a question you can ask.
 *
 * It is the same rule `MountayaElementsProvider` keeps for its own fields (state one and you
 * own it, omit one and you inherit it) made askable. A consumer writing their own composed
 * host reaches for this; a consumer writing a surface reaches for `useMountaya`, because a
 * surface with no client has nothing to render and should say so rather than render empty.
 */
export const useMountayaOrNull = (): Mountaya | null => use(MountayaContext)?.client ?? null;
